import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { t } from "../../../i18n";
import { AdminMfaProvider } from "../../../http/protocol";
import { getContainer } from "../../../ioc/IOCSetup";
import { MintedSecretDisplay } from "./MintedSecretDisplay";
import { httpErrorMessage } from "./httpErrorMessage";

const OTP_LENGTH = 6;

interface Props {
  /** Decides whether a code has to be mailed before the admin can answer with one. */
  mfaProvider?: AdminMfaProvider;
  /** Called once the token exists, so the list behind the dialog reflects it before it is closed. */
  onMinted: () => void;
  onClose: () => void;
}

/**
 * Collects the label and the second factor a mint needs, then hands over the minted value.
 *
 * The dialog holds that value only for as long as it is open: the caller mounts it on open and
 * unmounts it on close, so nothing keeps a copy once the admin dismisses it.
 */
export function MintTokenDialog({ mfaProvider, onMinted, onClose }: Props): JSX.Element {
  const service = useMemo(() => getContainer().getAdminPersonalTokenService(), []);
  const [label, setLabel] = useState("");
  const [otp, setOtp] = useState("");
  const [minting, setMinting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [secret, setSecret] = useState<string | undefined>(undefined);

  const codeMustBeSent = mfaProvider !== AdminMfaProvider.AUTHENTICATOR_APP;
  const canMint = label.trim().length > 0 && otp.length === OTP_LENGTH && !minting;

  async function sendCode(): Promise<void> {
    setSendingCode(true);
    setError(undefined);
    try {
      await service.sendStepUpOtp();
      setCodeSent(true);
    } catch (sendError) {
      setError(httpErrorMessage(sendError, t("api-tokens.dialog.send-code-failed", undefined, "Could not send a verification code.")));
    } finally {
      setSendingCode(false);
    }
  }

  async function mint(): Promise<void> {
    setMinting(true);
    setError(undefined);
    try {
      const response = await service.mint({ label: label.trim(), otp });
      // The token exists from this point on and has taken a slot, so the list behind the dialog is
      // told either way. A response the value never reached would otherwise return the admin to the
      // form they just submitted, with nothing to say that a token had in fact been created.
      onMinted();
      if (response.secret === undefined || response.secret.length === 0) {
        setError(
          t(
            "api-tokens.dialog.secret-missing",
            undefined,
            "The API token was created, but its value did not reach this page. Revoke it and create another one.",
          ),
        );
        return;
      }
      setSecret(response.secret);
    } catch (mintError) {
      setError(httpErrorMessage(mintError, t("api-tokens.dialog.mint-failed", undefined, "Could not create the API token.")));
    } finally {
      setMinting(false);
    }
  }

  return (
    <Modal
      show={true}
      onHide={onClose}
      aria-labelledby="mint-token-dialog-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="mint-token-dialog-title">
          {secret ? t("api-tokens.secret.title", undefined, "Copy your API token now") : t("api-tokens.dialog.title", undefined, "New API token")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {secret ? (
          <MintedSecretDisplay secret={secret} />
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="mint-token-label">{t("api-tokens.dialog.label-field", undefined, "Label")}</label>
              <input
                id="mint-token-label"
                data-testid="mint-token-label"
                className="form-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
              <small className="text-muted">
                {t(
                  "api-tokens.dialog.label-help",
                  undefined,
                  "Name the machine or client this API token is for. The label is the only way to tell them apart later.",
                )}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="mint-token-otp">{t("api-tokens.dialog.otp-field", undefined, "Verification code")}</label>
              <input
                id="mint-token-otp"
                data-testid="mint-token-otp"
                className="form-control"
                type="text"
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH}
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\s/g, ""))}
              />
              <small className="text-muted">
                {codeMustBeSent
                  ? t("api-tokens.dialog.otp-help-email", undefined, "Send a code to your e-mail address, then enter it here.")
                  : t("api-tokens.dialog.otp-help-app", undefined, "Enter the current 6-digit code from your authenticator app.")}
              </small>
            </div>

            {codeMustBeSent && (
              <div className="form-group">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-testid="mint-token-send-code"
                  disabled={sendingCode}
                  onClick={() => sendCode()}
                >
                  {t("api-tokens.dialog.send-code", undefined, "Send code")}
                </button>
                {codeSent && (
                  <div
                    className="text-success mt-2"
                    data-testid="mint-token-code-sent"
                  >
                    {t("api-tokens.dialog.code-sent", undefined, "A verification code has been sent to your e-mail address.")}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {error && (
          <div
            className="alert alert-danger"
            role="alert"
            data-testid="mint-token-error"
          >
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {secret ? (
          <button
            type="button"
            className="btn btn-primary"
            data-testid="mint-token-done"
            onClick={onClose}
          >
            {t("api-tokens.dialog.done", undefined, "Done")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="mint-token-cancel"
              onClick={onClose}
            >
              {t("button.cancel", undefined, "Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="mint-token-submit"
              disabled={!canMint}
              onClick={() => mint()}
            >
              {t("api-tokens.dialog.create", undefined, "Create API token")}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
